import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import MainContainer from '../../../components/MainContainer';
import { makeStyles } from '@material-ui/core';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Box,
  Autocomplete,
  TextField,
  Button,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Tooltip,
  CircularProgress,
  Backdrop
} from '@mui/material';
import Title from '../../../components/Title';
import IndemnitySettingService from '../../../services/IndemnitySettingService';
import { useParams } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import * as XLSX from 'xlsx';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    elevation: 2,
    borderRadius: '8px',
    marginTop: '2%',
  },
  tabsContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  autocompleteCell: {
    minWidth: '250px',
  },
  amountCell: {
    fontWeight: 'bold',
  },
  successDialog: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  successIcon: {
    fontSize: '64px',
    color: '#4caf50',
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: '64px',
    color: '#f44336',
    marginBottom: theme.spacing(2),
  },
  loadingBackdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: theme.spacing(2),
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: '#555',
    fontSize: '1rem',
    fontWeight: 500,
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`missionary-tabpanel-${index}`}
      aria-labelledby={`missionary-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers de fusion (dates, regroupement par destination_id)
// ─────────────────────────────────────────────────────────────

const parseDateDMY = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map(Number);
  if (isNaN(d) || isNaN(m) || isNaN(y)) return null;
  return new Date(y, m - 1, d);
};

/**
 * ✅ CORRECTION MAJEURE : la clé doit être UNIQUE par missionnaire.
 * Deux missionnaires peuvent avoir la même destination_id (ex. "5"),
 * donc on préfixe avec missionary_id.
 * Format : "userId::destinationId" ou "userId::tmp|...|index"
 */
const getDestinationKey = (dest, fallbackIndex = 0) => {
  const userId = dest?.missionary_id ?? "";

  if (
    dest?.mission_timesheet_destination_id !== undefined &&
    dest?.mission_timesheet_destination_id !== null &&
    String(dest.mission_timesheet_destination_id) !== ""
  ) {
    return `${userId}::${String(dest.mission_timesheet_destination_id)}`;
  }

  return [
    `${userId}::tmp`,
    dest?.commune_depart ?? "",
    dest?.commune_arrivee ?? "",
    dest?.date_depart ?? "",
    dest?.date_fin ?? "",
    fallbackIndex,
  ].join("|");
};

/**
 * Regroupe une liste de destinations en une seule ligne par
 * mission_timesheet_destination_id (au sein d'un même missionnaire) :
 *  - la nuitée (night_number) est PRISE EN BASE, PAS cumulée
 *  - fusion dates min → max
 *  - conservation du premier indemnity_id non "0" trouvé
 *  - clé stable et unique par missionnaire via getDestinationKey
 */
const groupDestinationsByDestinationId = (destinations) => {
  if (!Array.isArray(destinations)) return [];

  const groups = new Map();

  destinations.forEach((dest, index) => {
    const key = getDestinationKey(dest, index);

    if (!groups.has(key)) {
      groups.set(key, {
        _key: key,
        mission_timesheet_destination_id: dest?.mission_timesheet_destination_id,
        commune_depart: dest?.commune_depart,
        commune_arrivee: dest?.commune_arrivee,
        night_number: null,
        date_depart: dest?.date_depart || null,
        date_fin: dest?.date_fin || null,
        indemnity_id: dest?.indemnity_id,
        missionary_id: dest?.missionary_id,
      });
    }

    const g = groups.get(key);

    const newNight = dest?.night_number;
    if (
      newNight !== undefined &&
      newNight !== null &&
      String(newNight) !== "" &&
      (g.night_number === null || String(g.night_number) === "")
    ) {
      g.night_number = newNight;
    }

    const curId = g.indemnity_id;
    const newId = dest?.indemnity_id;
    if (
      (curId === undefined || curId === null || String(curId) === "0") &&
      newId !== undefined && newId !== null && String(newId) !== "0"
    ) {
      g.indemnity_id = newId;
    }

    const dStart = parseDateDMY(dest?.date_depart);
    const dEnd   = parseDateDMY(dest?.date_fin);
    const gStart = parseDateDMY(g.date_depart);
    const gEnd   = parseDateDMY(g.date_fin);

    if (dStart && (!gStart || dStart < gStart)) {
      g.date_depart = dest.date_depart;
    }
    if (dEnd && (!gEnd || dEnd > gEnd)) {
      g.date_fin = dest.date_fin;
    }

    if (!g.commune_depart && dest?.commune_depart) g.commune_depart = dest.commune_depart;
    if (!g.commune_arrivee && dest?.commune_arrivee) g.commune_arrivee = dest.commune_arrivee;
  });

  return Array.from(groups.values()).map((g) => ({
    ...g,
    night_number: g.night_number !== null ? g.night_number : 0,
  }));
};

const IndemnityMissionConfirm = (props) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [missionaryList, setMissionaryList] = useState([]);
  const [destinationsByUser, setDestinationsByUser] = useState({});
  const [mission, setMission] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [dailyindemnity, setDailyindemnity] = useState([]);
  const [selectedIndemnities, setSelectedIndemnities] = useState({});
  const missionid = props.missionid;
  const [isloading, setIsloading] = useState(false);
  const timesheet_id = props.timesheet_id;
  const [state, setState] = useState("");
  const [observation, setObservation] = useState("");
  const { mission_planning_id } = useParams();

  const [type, setType] = useState("");

  const [isPageLoading, setIsPageLoading] = useState(true);

  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [downloadingPdf, setDownloadingPdf] = useState({});
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const hasInitializedSelection = useRef(false);

  // Memoization des destinations groupées par utilisateur
  const groupedDestinationsByUser = useMemo(() => {
    const result = {};
    missionaryList.forEach((missionary) => {
      const raw = destinationsByUser[missionary.user_id] || [];
      // On force missionary_id sur chaque destination brute pour garantir la clé
      const rawWithUser = raw.map(d => ({ ...d, missionary_id: missionary.user_id }));
      result[missionary.user_id] = groupDestinationsByDestinationId(rawWithUser);
    });
    return result;
  }, [missionaryList, destinationsByUser]);

  const getGroupedDestinationsForUser = useCallback((missionIndex) => {
    if (!missionaryList.length || missionIndex >= missionaryList.length) {
      return [];
    }
    const missionary = missionaryList[missionIndex];
    return groupedDestinationsByUser[missionary.user_id] || [];
  }, [missionaryList, groupedDestinationsByUser]);

  const formatDateDisplay = (dateDepart, dateFin) => {
    if (!dateDepart && !dateFin) return 'Non spécifié';
    if (dateDepart === dateFin) return dateDepart;
    return `${dateDepart} - ${dateFin}`;
  };

  const getDefaultIndemnity = (destination) => {
    if (!dailyindemnity.length) return null;
    const nights = parseInt(destination?.night_number, 10) || 0;

    if (nights === 1) {
      return dailyindemnity.find(item => item.is_default === "1") || null;
    } else {
      return dailyindemnity.find(item => item.is_default_nigth === "1") || null;
    }
  };

  const resolveIndemnityForDestination = (destination) => {
    if (!dailyindemnity.length) return null;

    const savedId = destination?.indemnity_id;

    if (savedId !== undefined && savedId !== null && String(savedId) !== "0") {
      const found = dailyindemnity.find(
        item => String(item.indemnity_id) === String(savedId)
      );
      if (found) return found;
    }

    return getDefaultIndemnity(destination);
  };

  // Chargement initial
  useEffect(() => {
    let isMounted = true;
    setIsPageLoading(true);

    const fetchData = async () => {
      try {
        const [detailResult, activeIndemnityResult] = await Promise.all([
          IndemnitySettingService.IndemnityDetail(missionid, timesheet_id),
          IndemnitySettingService.Activeindemnity()
        ]);

        if (!isMounted) return;

        if (detailResult && detailResult.data) {
          const missionaries = [];
          const destinationsMap = {};

          Object.keys(detailResult.data).forEach(userId => {
            const userData = detailResult.data[userId];

            missionaries.push({
              user_id: userData.user_id,
              nom: userData.nom,
              last_name: userData.last_name,
              function_code: userData.function_code
            });

            if (userData.destinations && Array.isArray(userData.destinations)) {
              destinationsMap[userId] = userData.destinations.map(dest => ({
                mission_timesheet_destination_id: dest.mission_timesheet_destination_id,
                commune_depart: dest.commune_depart,
                commune_arrivee: dest.commune_arrivee,
                night_number: dest.night_number,
                date_depart: dest.date_depart,
                date_fin: dest.date_fin,
                indemnity_id: dest.indemnity_id,
                missionary_id: userId // ✅ toujours défini
              }));
            }
          });

          setState(detailResult.state);
          setObservation(detailResult.observation);
          setMissionaryList(missionaries);
          setDestinationsByUser(destinationsMap);
          setType(detailResult.type);

          if (detailResult.mission) {
            setMission(detailResult.mission);
          }
        }

        if (activeIndemnityResult && activeIndemnityResult.data) {
          setDailyindemnity(activeIndemnityResult.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement initial:', error);
        if (isMounted) {
          setErrorMessage(error?.message || "Une erreur est survenue lors du chargement des données");
          setOpenErrorDialog(true);
        }
      } finally {
        if (isMounted) {
          setIsPageLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [missionid, timesheet_id]);

  // Initialisation UNIQUE des sélections
  useEffect(() => {
    if (
      Object.keys(destinationsByUser).length === 0 ||
      missionaryList.length === 0 ||
      dailyindemnity.length === 0
    ) {
      return;
    }

    if (hasInitializedSelection.current) {
      return;
    }

    const defaultIndemnities = {};

    missionaryList.forEach((missionary) => {
      const grouped = groupedDestinationsByUser[missionary.user_id] || [];

      grouped.forEach((destination) => {
        const key = destination._key;
        const resolvedIndemnity = resolveIndemnityForDestination(destination);

        if (resolvedIndemnity) {
          defaultIndemnities[key] = {
            indemnity_id: resolvedIndemnity.indemnity_id,
            daily_amount: resolvedIndemnity.daily_amount,
            indemnity_type: resolvedIndemnity.indemnity_type,
            mission_timesheet_destination_id: destination.mission_timesheet_destination_id,
            user_id: missionary.user_id,
            district_depart: destination.commune_depart,
            district_arrivee: destination.commune_arrivee,
            date_depart: destination.date_depart,
            date_fin: destination.date_fin,
            night_number: destination.night_number
          };
        }
      });
    });

    setSelectedIndemnities(defaultIndemnities);
    hasInitializedSelection.current = true;
  }, [destinationsByUser, missionaryList, dailyindemnity, groupedDestinationsByUser]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // ✅ Mise à jour ciblée d'UNE seule ligne (clé unique par missionnaire)
  const handleIndemnityChange = (destination, newValue) => {
    if (!destination) return;
    const key = destination._key;

    if (!newValue) {
      setSelectedIndemnities((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    const missionary = missionaryList.find(
      m => String(m.user_id) === String(destination.missionary_id)
    ) || {};

    setSelectedIndemnities((prev) => ({
      ...prev,
      [key]: {
        indemnity_id: newValue.indemnity_id,
        daily_amount: newValue.daily_amount,
        indemnity_type: newValue.indemnity_type,
        mission_timesheet_destination_id: destination.mission_timesheet_destination_id,
        user_id: missionary.user_id || destination.missionary_id,
        district_depart: destination.commune_depart,
        district_arrivee: destination.commune_arrivee,
        date_depart: destination.date_depart,
        date_fin: destination.date_fin,
        night_number: destination.night_number
      }
    }));
  };

  const calculateRowAmount = (destination) => {
    if (!destination) return 0;
    const key = destination._key;
    const selectedIndemnity = selectedIndemnities[key];

    if (!selectedIndemnity || !selectedIndemnity.daily_amount) {
      return 0;
    }

    const dailyAmount = parseFloat(selectedIndemnity.daily_amount) || 0;
    const nightNumber = parseInt(destination.night_number, 10) || 0;

    if (nightNumber === 0) {
      return dailyAmount;
    }
    return dailyAmount * nightNumber;
  };

  const calculateMissionaryTotal = (missionIndex) => {
    const destinations = getGroupedDestinationsForUser(missionIndex);
    let total = 0;
    destinations.forEach((destination) => {
      total += calculateRowAmount(destination);
    });
    return total;
  };

  const calculateGeneralTotal = () => {
    let total = 0;
    missionaryList.forEach((missionary, missionIndex) => {
      total += calculateMissionaryTotal(missionIndex);
    });
    return total;
  };

  const buildPdfUrl = (missionary) => {
    let pdfFileName;

    if (type === "planning") {
      pdfFileName = `indemnite_${missionary.last_name}_${mission_planning_id}.pdf`;
    } else {
      pdfFileName = `indemnite_${missionary.last_name}_${timesheet_id}.pdf`;
    }

    return `${process.env.REACT_APP_API_URL_FILEPGI}/Indemnite/${pdfFileName}`;
  };

  const downloadPdfAsPromise = (pdfUrl, fileName) => {
    return fetch(pdfUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      });
  };

  const handleDownloadPdfForMissionary = (missionary) => {
    if (!missionary || !missionary.last_name) {
      setErrorMessage("Nom du missionnaire manquant");
      setOpenErrorDialog(true);
      return;
    }

    setDownloadingPdf(prev => ({ ...prev, [missionary.user_id]: true }));

    try {
      const pdfUrl = buildPdfUrl(missionary);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      setErrorMessage(`Erreur lors du téléchargement du PDF pour ${missionary.last_name || missionary.nom}`);
      setOpenErrorDialog(true);
    } finally {
      setDownloadingPdf(prev => ({ ...prev, [missionary.user_id]: false }));
    }
  };

  const handleExportExcel = () => {
    setDownloadingExcel(true);

    try {
      const excelUrl = buildExcelUrl();
      window.open(excelUrl, '_blank');
    } catch (error) {
      console.error("Erreur lors du téléchargement de l'Excel:", error);
      setErrorMessage("Erreur lors du téléchargement du fichier Excel");
      setOpenErrorDialog(true);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const buildExcelUrl = () => {
    let excelFileName;

    if (type === "planning") {
      excelFileName = `recap_indemnites_${mission_planning_id}.xlsx`;
    } else {
      excelFileName = `recap_indemnites_${timesheet_id}.xlsx`;
    }

    return `${process.env.REACT_APP_API_URL_FILEPGI}/Indemnite/${excelFileName}`;
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
    navigate('/mission/fuel_forecast');
  };

  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
  };

  const handleToValidate = async () => {
    setIsloading(true);

    const indemnityData = {
      selectedIndemnities: selectedIndemnities,
      missionid: missionid,
      timesheet_id: timesheet_id,
      generateFiles: true
    };

    try {
      const result = await IndemnitySettingService.hisrtoryindemnity(indemnityData);

      if (!result || !result.status) {
        setErrorMessage(result?.message || "Une erreur est survenue lors de l'enregistrement des indemnités");
        setOpenErrorDialog(true);
        setIsloading(false);
        return;
      }

      if (missionaryList && missionaryList.length > 0) {
        const downloadingState = {};
        missionaryList.forEach(m => {
          downloadingState[m.user_id] = true;
        });
        setDownloadingPdf(downloadingState);

        const downloadPromises = missionaryList.map((missionary) => {
          if (!missionary || !missionary.last_name) {
            return Promise.resolve();
          }
          const pdfUrl = buildPdfUrl(missionary);
          const fileName = pdfUrl.split('/').pop();
          return downloadPdfAsPromise(pdfUrl, fileName)
            .catch(err => {
              console.error(`Erreur téléchargement PDF pour ${missionary.last_name}:`, err);
              return null;
            });
        });

        await Promise.all(downloadPromises);

        const resetState = {};
        missionaryList.forEach(m => {
          resetState[m.user_id] = false;
        });
        setDownloadingPdf(resetState);
      }

      setSuccessMessage(result.message || "Les indemnités ont été enregistrées avec succès");
      setOpenSuccessDialog(true);
      setIsloading(false);

    } catch (error) {
      console.error('Erreur globale:', error);
      setErrorMessage(error?.message || "Une erreur est survenue lors de la communication avec le serveur");
      setOpenErrorDialog(true);
      setIsloading(false);
    }
  };

  if (isPageLoading) {
    return (
      <div>
        <Paper className={classes.paper} variant="outlined">
          <div className={classes.loadingContainer}>
            <CircularProgress size={60} thickness={4} />
            <Typography className={classes.loadingText}>
              Chargement des indemnités de mission en cours...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Veuillez patienter pendant la récupération des données
            </Typography>
          </div>
        </Paper>
      </div>
    );
  }

  return (
    <div>
      <Paper className={classes.paper} variant="outlined">
        <div>
          <h3>Motif : {mission.mission_title}</h3>
        </div>

        <div>
          {state === "REFUSED" &&
            <Alert severity="error">
              Observation : {observation}
            </Alert>
          }
        </div>
        <div>
          {state === "PENDING" &&
            <Alert severity="info">
              Cette indemnité est en attente de validation.
            </Alert>
          }
        </div>
        <div>
          {state === "CONFIRMED" &&
            <Alert severity="success">
              Cette indemnité a déjà été validée.
            </Alert>
          }
        </div>

        <Box className={classes.tabsContainer}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="missionnaire tabs"
          >
            {missionaryList.map((missionary, index) => (
              <Tab
                key={missionary.user_id}
                label={missionary.last_name || missionary.nom || `Missionnaire ${index + 1}`}
                id={`missionary-tab-${index}`}
                aria-controls={`missionary-tabpanel-${index}`}
              />
            ))}
            <Tab
              label="Récapitulatif"
              id="recap-tab"
              aria-controls="recap-tabpanel"
            />
          </Tabs>

          {missionaryList.map((missionary, missionIndex) => {
            const groupedDestinations = getGroupedDestinationsForUser(missionIndex);

            return (
              <TabPanel key={missionary.user_id} value={selectedTab} index={missionIndex}>
                <Title>{`Indemnités - ${missionary.nom || ''}`}</Title>

                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Lieu de départ</TableCell>
                        <TableCell>Lieu d'arrivée</TableCell>
                        <TableCell>Nuitée</TableCell>
                        <TableCell className={classes.autocompleteCell}>Indemnité unitaire</TableCell>
                        <TableCell className={classes.amountCell}>Montant</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedDestinations.map((destination) => {
                        const key = destination._key;
                        const selectedIndemnity = selectedIndemnities[key];
                        const amount = calculateRowAmount(destination);

                        return (
                          <TableRow key={key}>
                            <TableCell>
                              {formatDateDisplay(destination.date_depart, destination.date_fin)}
                            </TableCell>
                            <TableCell>{destination.commune_depart || 'N/A'}</TableCell>
                            <TableCell>{destination.commune_arrivee || 'N/A'}</TableCell>
                            <TableCell>
                              {destination.night_number || '0'}
                              {destination.night_number === 0 && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="textSecondary"
                                  sx={{ fontStyle: 'italic' }}
                                >
                                  (Déplacement journalier)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                id={`indemnity-select-${key}`}
                                options={dailyindemnity}
                                value={
                                  selectedIndemnity
                                    ? dailyindemnity.find(
                                        item =>
                                          String(item.indemnity_id) ===
                                          String(selectedIndemnity.indemnity_id)
                                      ) || null
                                    : null
                                }
                                onChange={(event, newValue) => {
                                  handleIndemnityChange(destination, newValue);
                                }}
                                getOptionLabel={(option) => option.indemnity_type || ""}
                                size="small"
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Sélectionnez une indemnité"
                                    variant="outlined"
                                    size="small"
                                  />
                                )}
                                isOptionEqualToValue={(option, value) =>
                                  String(option?.indemnity_id) === String(value?.indemnity_id)
                                }
                              />
                              {selectedIndemnity && selectedIndemnity.daily_amount && (
                                <div style={{ marginTop: '5px', fontSize: '0.85rem', color: '#555' }}>
                                  Montant journalier : <strong>{selectedIndemnity.daily_amount} Ar</strong>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className={classes.amountCell}>
                              {amount.toLocaleString('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })} Ar
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow style={{ fontWeight: 'bold' }}>
                        <TableCell colSpan={5} align="right">
                          Total pour {missionary.last_name || missionary.nom}:
                        </TableCell>
                        <TableCell className={classes.amountCell}>
                          {calculateMissionaryTotal(missionIndex).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })} Ar
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>
            );
          })}

          <TabPanel value={selectedTab} index={missionaryList.length}>
            <Title>Récapitulatif des indemnités</Title>

            <TableContainer component={Paper} className={classes.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Missionnaire</TableCell>
                    <TableCell>Total des indemnités</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {missionaryList.map((missionary, index) => {
                    const totalAmount = calculateMissionaryTotal(index);
                    return (
                      <TableRow key={missionary.user_id}>
                        <TableCell>
                          <strong>{missionary.last_name || missionary.nom || ''}</strong>
                          {missionary.function_code && (
                            <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '8px' }}>
                              ({missionary.function_code})
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <strong>
                            {totalAmount.toLocaleString('fr-FR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })} Ar
                          </strong>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedTab(index)}
                              style={{ marginRight: '8px' }}
                            >
                              Voir détails
                            </Button>

                            {(state === "PENDING" || state === "CONFIRMED") && (
                              <Tooltip title={`Télécharger le PDF pour ${missionary.last_name || missionary.nom}`}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  startIcon={<PictureAsPdfIcon />}
                                  onClick={() => handleDownloadPdfForMissionary(missionary)}
                                  disabled={downloadingPdf[missionary.user_id]}
                                  style={{
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    minWidth: '100px'
                                  }}
                                >
                                  {downloadingPdf[missionary.user_id] ? (
                                    <span>Chargement...</span>
                                  ) : (
                                    'PDF'
                                  )}
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    <TableCell>Total Général</TableCell>
                    <TableCell>
                      {calculateGeneralTotal().toLocaleString('fr-FR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} Ar
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Grid container spacing={3}>
              <Grid item xs={12}></Grid>
              <Grid item xs={6}>
                <Tooltip title="Exporter le récapitulatif en Excel">
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportExcel}
                    disabled={downloadingExcel || missionaryList.length === 0}
                    style={{
                      backgroundColor: '#217346',
                      color: 'white',
                      minWidth: '200px'
                    }}
                  >
                    {downloadingExcel ? (
                      <span>Export en cours...</span>
                    ) : (
                      'Exporter en Excel'
                    )}
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item xs={3}></Grid>
              <Grid item xs={3}>
                {(!state || state === "REFUSED") &&
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleToValidate}
                    loading={isloading}
                  >
                    Envoyer pour validation
                  </LoadingButton>
                }
              </Grid>
            </Grid>
          </TabPanel>
        </Box>

        <Dialog
          open={openSuccessDialog}
          onClose={handleCloseSuccessDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ backgroundColor: '#4caf50', color: 'white', textAlign: 'center' }}>
            Succès !
          </DialogTitle>
          <DialogContent className={classes.successDialog}>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography variant="h6" style={{ marginBottom: '16px', color: '#2e7d32' }}>
              {successMessage}
            </Typography>
            <Typography variant="body2" style={{ marginBottom: '24px', color: '#666' }}>
              L'indemnité de mission a été envoyée avec succès. Tous les PDF ont été téléchargés. Votre indemnité de mission est en attente de validation maintenant.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSuccessDialog} color="primary">
              Fermer
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openErrorDialog}
          onClose={handleCloseErrorDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle style={{ backgroundColor: '#f44336', color: 'white', textAlign: 'center' }}>
            Erreur
          </DialogTitle>
          <DialogContent className={classes.successDialog}>
            <ErrorIcon className={classes.errorIcon} />
            <Typography variant="h6" style={{ marginBottom: '16px', color: '#c62828' }}>
              Une erreur est survenue
            </Typography>
            <Typography variant="body2" style={{ marginBottom: '24px', color: '#666' }}>
              {errorMessage}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseErrorDialog} color="primary">
              Fermer
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </div>
  );
};

export default IndemnityMissionConfirm;